/* eslint-disable no-console */
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  Operation,
  createHttpLink,
  from,
  split,
} from '@apollo/client';
import {
  ApolloCache,
  ModifierDetails,
  StoreObject,
} from '@apollo/client/cache';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { DefinitionNode, Kind, OperationDefinitionNode } from 'graphql';
import { createClient } from 'graphql-ws';

import { AnalyticsTracker } from '@backstage/core-plugin-api';

import { Error as GraphQLErrorResult } from '../graphql';
import { PageLoadTracker } from './PageLoadTracker';
import { BUILD_COMMIT_HASH } from './buildInfo.generated';
import { namedMerger } from './namedMerger';
import { operationQueueManager } from './operationQueue';

const generateRequestId = () => {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
};

const pageLoadTracker = new PageLoadTracker();

const UNSAFE_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

const deepOmit = <T extends object>(value: T, key: string): T => {
  const _omit = (currentValue: any): any => {
    if (Array.isArray(currentValue)) {
      return currentValue.map(i => _omit(i));
    } else if (typeof currentValue === 'object' && currentValue !== null) {
      return Object.keys(currentValue).reduce((newObject, k) => {
        if (k === key || UNSAFE_KEYS.has(k)) return newObject;
        return Object.assign(newObject, { [k]: _omit(currentValue[k]) });
      }, {});
    }
    return currentValue;
  };
  return _omit(value) as T;
};

export type GraphQLResult = { __typename?: string };
type ValueOfTypename<T extends GraphQLResult> = T['__typename'];

export function isType<
  Result extends GraphQLResult,
  Typename extends ValueOfTypename<Result>,
>(
  result: Result | null | undefined,
  typename: Typename,
): result is Extract<Result, { __typename: Typename }> {
  return result?.__typename === typename;
}

export function getError<Result extends GraphQLResult>(
  result: Result | null | undefined,
): Error | null {
  if (isError(result)) {
    return new Error(
      result.message ??
        result.code?.replaceAll('_', ' ') ??
        'Something went wrong',
    );
  }
  return null;
}

export function getFunctionError<Result extends GraphQLResult>(
  result: Result | null | undefined,
): Error | null {
  const error = getError(result);
  if (error) {
    return new Error(
      error.message.replace(/^Function failed \[/, '').replace(/\]$/, ''),
    );
  }

  return null;
}

export function getTypeOrNull<Result extends GraphQLResult>(
  result: Result | null | undefined,
): Extract<
  Exclude<Result, { __typename: 'Error' }>,
  { __typename: ValueOfTypename<Result> }
> | null {
  if (!result) {
    return null;
  }

  if (isError(result)) {
    return null;
  }

  if (!result.__typename) {
    return null;
  }

  return result as Extract<
    Exclude<Result, { __typename: 'Error' }>,
    { __typename: ValueOfTypename<Result> }
  >;
}

export const getTypeOrUndef = <T>(
  value: T | null | undefined,
): T | undefined => {
  if (!value || isError(value)) {
    return undefined;
  }

  return value;
};

export function isError<Result extends GraphQLResult>(
  result: Result | null | undefined,
): result is Extract<Result, { __typename: 'Error' }> &
  Partial<GraphQLErrorResult> {
  return result?.__typename === 'Error';
}

export const evictTypeFields = <T>(
  cache: ApolloCache<Record<string, StoreObject>>,
  field: keyof T,
) => {
  cache.modify({
    fields: {
      [field]: (_: any, { DELETE }: ModifierDetails) => {
        return DELETE;
      },
    },
  });
};

function isOperationDefinition(
  node: DefinitionNode,
): node is OperationDefinitionNode {
  return node.kind === Kind.OPERATION_DEFINITION;
}

export const createApolloClient = ({
  token,
  uri,
  region,
  analyticsApi,
}: {
  token?: string;
  uri?: string;
  region?: 'EU' | 'US';
  analyticsApi?: AnalyticsTracker;
}) => {
  if (!token || !uri || !region) {
    return new ApolloClient({
      cache: new InMemoryCache(),
      link: operationQueueManager.createQueueLink(),
    });
  }

  const httpLink = createHttpLink({ uri });

  // Detect rapid reconnection loops and stop them
  const reconnectLimiter = {
    closeTimes: [] as number[],
    maxCloses: 20,
    windowMs: 5000,
    shouldStop: () => {
      const now = Date.now();
      // Remove old entries outside the window
      reconnectLimiter.closeTimes = reconnectLimiter.closeTimes.filter(
        t => now - t < reconnectLimiter.windowMs,
      );
      return reconnectLimiter.closeTimes.length >= reconnectLimiter.maxCloses;
    },
    recordClose: () => {
      reconnectLimiter.closeTimes.push(Date.now());
      // Keep array bounded - we only need maxCloses entries
      if (reconnectLimiter.closeTimes.length > reconnectLimiter.maxCloses) {
        reconnectLimiter.closeTimes.shift();
      }
    },
  };

  const wsClient = createClient({
    url: `wss://graphql-websocket.confidence.dev/websocket`,
    connectionParams: {
      authorization: token ? `Bearer ${token}` : '',
    },
    on: {
      closed: () => {
        reconnectLimiter.recordClose();
        if (reconnectLimiter.shouldStop()) {
          // Too many closes in a short time - likely a persistent error.
          // Dispose to prevent infinite reconnection loop.
          wsClient.dispose();
        }
      },
    },
  });

  const wsLink = new GraphQLWsLink(wsClient);

  const omitTypenameLink = new ApolloLink((operation, forward) => {
    if (operation.variables) {
      operation.variables = deepOmit(operation.variables, '__typename');
    }
    return forward(operation);
  });

  const authLink = setContext(async (_, { headers }) => {
    return {
      headers: {
        ...headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  const requestIdLink = new ApolloLink((operation, forward) => {
    const requestId = generateRequestId();
    operation.setContext({
      requestId,
      headers: {
        ...operation.getContext().headers,
        'x-confidence-request-id': requestId,
        'x-confidence-build-version': BUILD_COMMIT_HASH,
      },
    });
    return forward(operation);
  });

  const errorLink = onError(({ operation, graphQLErrors, networkError }) => {
    if (graphQLErrors)
      graphQLErrors.forEach(({ message, path }) =>
        console.error(`[GraphQL error]: Message: ${message}, Path: ${path}`),
      );

    if (networkError) console.error(`[Network error]: ${networkError}`);
    if (analyticsApi && networkError) {
      analyticsApi.captureEvent('graphql-error', operation.operationName, {
        attributes: {
          networkError: networkError?.message ?? 'Unknown network error',
          requestId: operation.getContext().requestId,
        },
      });
    }
  });

  // Configure retries for network errors. GraphQL errors are currently not picked up
  // by the retry link.
  // (source https://www.apollographql.com/docs/react/api/link/apollo-link-retry#overview)
  const retryLink = new RetryLink({
    attempts: (count, operation: Operation) => {
      const isMutation = operation.query.definitions.some(
        def => isOperationDefinition(def) && def.operation === 'mutation',
      );
      if (isMutation) {
        // Never retry mutations, in case of non-repeatable side effects.
        return false;
      }

      return count <= 10;
    },
  });

  // Measure the time it takes to complete an operation
  // and send it to analytics
  const roundTripLink = new ApolloLink((operation, forward) => {
    const definition = getMainDefinition(operation.query);

    operation.setContext({ start: performance.now() });

    // Don't track subscriptions for page load - they're long-lived
    const isSubscription =
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription';
    if (!isSubscription) {
      pageLoadTracker.onRequestStart();
    }

    return forward(operation).map(data => {
      // Mark request as complete on first response (queries/mutations emit once then complete)
      if (!isSubscription) {
        pageLoadTracker.onRequestEnd();
      }

      const pageLoadId = pageLoadTracker.getPageLoadId();
      const time = performance.now() - operation.getContext().start;
      if (analyticsApi) {
        analyticsApi.captureEvent(
          'graphql-request-timing',
          operation.operationName,
          {
            value: time,
            attributes: {
              requestId: operation.getContext().requestId,
              path: pageLoadTracker.getPath(),
              ...(pageLoadId ? { pageLoadId } : {}),
            },
          },
        );
      }
      return data;
    });
  });

  // The split function takes three parameters:
  //
  // * A function that's called for each operation to execute
  // * The Link to use for an operation if the function returns a "truthy" value
  // * The Link to use for an operation if the function returns a "falsy" value
  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink,
    httpLink,
  );

  const client = new ApolloClient({
    connectToDevTools: true,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
      },
    },
    link: from([
      requestIdLink,
      authLink,
      omitTypenameLink,
      errorLink,
      retryLink,
      roundTripLink,
      splitLink,
    ]),
    cache: new InMemoryCache({
      typePolicies: {
        AdminV1AccountInfo: {
          keyFields: false,
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
        WorkflowV1Workflow: {
          keyFields: ['name'],
        },
        WorkflowV1WorkflowInstance: {
          keyFields: ['name'],
          fields: {
            checks: {
              merge(_existing, incoming) {
                return incoming;
              },
            },
            moduleData2: {
              merge: true,
            },
          },
        },
        AudiencesDataAudience: {
          // prevent normalization
          keyFields: false,
        },
        WorkflowV1WorkflowSecret: {
          keyFields: ['name'],
        },
        WorkflowV1WorkflowVersion: {
          keyFields: ['name'],
        },
        IamV1OAuthApp: {
          keyFields: ['name'],
        },
        IamV1Client: {
          keyFields: ['name'],
        },
        IamV1ApiClient: {
          keyFields: ['name'],
        },
        IamV1Identity: {
          keyFields: ['name'],
        },
        IamV1Group: {
          keyFields: ['name'],
        },
        IamV1CryptoKey: {
          keyFields: ['name'],
        },
        IamV1Policy: {
          keyFields: ['name'],
        },
        IamV1Role: {
          keyFields: ['name'],
        },
        IamV1Agent: {
          keyFields: ['name'],
          fields: {
            reviewerSkill: {
              merge(_existing, incoming) {
                return incoming;
              },
            },
          },
        },
        IamV1CurrentUser: {
          keyFields: false,
        },
        IamV1CurrentUserAccountMembership: {
          keyFields: ['account'],
        },
        FgaV1ResourcePermission: {
          keyFields: ['name'],
        },
        ConnectorsV1FlagAppliedConnection: {
          keyFields: ['name'],
        },
        WorkflowV1Surface: {
          keyFields: ['name'],
          fields: {
            metricConfig: {
              merge: true,
            },
          },
        },
        WorkflowV1SurfaceIdentityBinding: {
          keyFields: ['name'],
        },
        WorkflowV1Initiative: {
          keyFields: ['name'],
        },
        CollectionsV1Collection: {
          keyFields: ['name'],
        },
        CollectionsV1CollectionEntry: {
          keyFields: ['name'],
        },
        WorkflowV1ExclusivityGroup: {
          keyFields: ['name'],
        },
        FlagsAdminV1Segment: {
          keyFields: ['name'],
        },
        FlagsAdminV1MaterializedSegment: {
          keyFields: ['name'],
        },
        FlagsAdminV1Environment: {
          keyFields: ['name'],
        },
        AiworkflowsV1SourceCodeRepository: {
          keyFields: ['name'],
        },
        FlagsAdminV1SegmentAllocation: {
          merge(existing, incoming, { mergeObjects }) {
            return mergeObjects(existing, incoming);
          },
        },
        FlagsTypesV1Targeting: {
          merge(_existing, incoming) {
            return incoming;
          },
        },
        FlagsAdminV1Flag: {
          keyFields: ['name'],
        },
        WorkflowV1Holdback: {
          keyFields: ['name'],
        },
        MetricsV1Metric: {
          keyFields: ['name'],
          fields: {
            measurementConfig: {
              merge(existing, incoming, { mergeObjects }) {
                if (incoming?.openWindow) {
                  return {
                    openWindow: mergeObjects(
                      existing?.openWindow,
                      incoming.openWindow,
                    ),
                    semiOpenWindow: null,
                    closedWindow: null,
                  };
                }
                if (incoming?.closedWindow) {
                  return {
                    closedWindow: mergeObjects(
                      existing?.closedWindow,
                      incoming.closedWindow,
                    ),
                    openWindow: null,
                    semiOpenWindow: null,
                  };
                }
                if (incoming?.semiOpenWindow) {
                  return {
                    semiOpenWindow: mergeObjects(
                      existing?.semiOpenWindow,
                      incoming.semiOpenWindow,
                    ),
                    openWindow: null,
                    closedWindow: null,
                  };
                }
                // This should not happen but we need to return something
                return incoming;
              },
            },
          },
        },
        MetricsV1AssignmentTable: {
          keyFields: ['name'],
        },
        MetricsV1DataWarehouse: {
          keyFields: ['name'],
        },
        MetricsV1DimensionTable: {
          keyFields: ['name'],
        },
        MetricsV1FactTable: {
          keyFields: ['name'],
        },
        MetricsV1EntityRelationTable: {
          keyFields: ['name'],
        },
        MetricsV1Entity: {
          keyFields: ['name'],
        },
        EventsAdminV1EventDefinition: {
          keyFields: ['name'],
        },
        FlagsAdminV1FlagRule: {
          keyFields: ['name'],
        },
        FlagsAdminV1FlagVariant: {
          keyFields: ['name'],
        },
        EventconnectorsV1Event: {
          keyFields: ['name'],
        },
        MetricsV1ExposureCalculation: {
          keyFields: ['name'],
        },
        MetricsV1Measurement: {
          keyFields: ['name'],
        },
        MetricsV1MeasurementCalculation: {
          keyFields: ['name'],
        },
        MetricsV1MeasurementTable: {
          keyFields: ['name'],
        },
        MetricsV1Retrigger: {
          keyFields: ['name'],
        },
        MetricsV1MetricRatioMetricSpec: {
          keyFields: false,
          merge: true,
        },
        MetricsV1ScheduledExposureCalculation: {
          keyFields: ['name'],
          fields: {
            exposureCalculations: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('exposureCalculations'),
            },
            status: {
              merge(_existing, incoming) {
                return incoming;
              },
            },
          },
        },
        MetricsV1ScheduledMeasurementCalculation: {
          keyFields: ['name'],
          fields: {
            measurementCalculations: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('measurementCalculations'),
            },
          },
        },
        MetricsV1ScheduledMetricCalculation: {
          keyFields: ['name'],
          fields: {
            metricCalculations: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('metricCalculations'),
            },
          },
        },
        AdminLabelsV1Label: {
          keyFields: ['name'],
        },
        ActivityV1Feed: {
          keyFields: ['name'],
        },
        ActivityV1FeedEntry: {
          keyFields: ['name'],
          merge: true,
        },
        ActivityV1Activity: {
          keyFields: ['name'],
          merge: true,
        },
        BillingV1StripeCustomer: {
          keyFields: ['name'],
        },
        MetricsV1MetricCalculation: {
          keyFields: ['name'],
        },
        IamV1User: {
          keyFields: ['name'],
        },
        IamV1UserInvitation: {
          keyFields: ['name'],
        },
        CollaborationV1CollaborativeDocument: {
          keyFields: ['name'],
        },
        CollaborationV1Attachment: {
          keyFields: ['name'],
        },
        CollaborationV1Presence: {
          keyFields: ['name'],
        },
        CollaborationV1Comment: {
          keyFields: ['name'],
          fields: {
            commentReplies: {
              keyArgs: ['filter'],
              merge: namedMerger('commentReplies'),
            },
          },
        },
        CollaborationV1CommentReply: {
          keyFields: ['name'],
        },
        CollaborationV1ReactionSummary: {
          keyFields: ['name'],
          fields: {
            emojiReactions: {
              merge(_existing, incoming) {
                return incoming;
              },
            },
          },
        },
        CollaborationV1Reaction: {
          keyFields: ['name'],
        },
        CollaborationV1AgentReviewPreview: {
          keyFields: ['name'],
        },
        InsightsV1InsightsDocument: {
          keyFields: ['name'],
        },
        InsightsV1SuggestedQuestion: {
          keyFields: false,
        },
        AbTestTreatment: {
          keyFields: ['name'],
        },
        AiworkflowsV1AiWorkflowInstance: {
          keyFields: ['name'],
        },
        AiworkflowsV1AiWorkflowSchedule: {
          keyFields: ['name'],
        },
        AiworkflowsV1AiWorkflowTrigger: {
          keyFields: ['name'],
        },
        StatsV2AnalysisAnalysisResult: {
          keyFields: ['name'],
        },
        StatsV2AnalysisStatsHypothesisResult: {
          keyFields: false,
        },
        StatsV2AnalysisStatsGroupStats: {
          keyFields: false,
        },
        // https://www.apollographql.com/docs/react/pagination/core-api/#merging-paginated-results
        Query: {
          fields: {
            activities: {
              keyArgs: ['resource', 'filter'],
              merge: namedMerger('activities'),
            },
            currentUser: {
              keyArgs: false,
              read: e => e,
              merge: (existing, incoming, { mergeObjects }) => {
                return mergeObjects(existing, incoming);
              },
            },
            workflowInstances: {
              keyArgs: ['parent', 'filter', 'orderBy'],
              merge: namedMerger('workflowInstances'),
            },
            holdbacks: {
              keyArgs: ['parent'],
              merge: namedMerger('holdbacks'),
            },
            surfaces: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('surfaces'),
            },
            surfaceIdentityBindings: {
              keyArgs: ['parent'],
              merge: namedMerger('surfaceIdentityBindings'),
            },
            exclusivityGroups: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('exclusivityGroups'),
            },
            collections: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('collections'),
            },
            collectionEntries: {
              keyArgs: ['parent'],
              merge: namedMerger('collectionEntries'),
            },
            insightsDocuments: {
              keyArgs: [],
              merge: namedMerger('insightsDocuments'),
            },
            workflowSecrets: {
              keyArgs: [],
              merge: namedMerger('workflowSecrets'),
            },
            workflows: {
              keyArgs: [],
              merge: namedMerger('workflows'),
            },
            workflowLogs: {
              keyArgs: ['parent'],
              merge: namedMerger('workflowLogs'),
            },
            workflowVersions: {
              keyArgs: ['parent'],
              merge: namedMerger('workflowVersions'),
            },
            oAuthApps: {
              keyArgs: [],
              merge: namedMerger('oAuthApps'),
            },
            measurements: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('measurements'),
            },
            metrics: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('metrics'),
            },
            flags: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('flags'),
            },
            apiClients: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('apiClients'),
            },
            agents: {
              keyArgs: ['filter'],
              merge: namedMerger('agents'),
            },
            identities: {
              keyArgs: ['filter'],
              merge: namedMerger('identities'),
            },
            labels: {
              keyArgs: ['filter'],
              merge: namedMerger('labels'),
            },
            groups: {
              keyArgs: ['filter'],
              merge: namedMerger('groups'),
            },
            clients: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('clients'),
            },
            segments: {
              keyArgs: ['filter', 'view', 'orderBy'],
              merge: namedMerger('segments'),
            },
            sourceCodeRepositories: {
              keyArgs: ['filter'],
              merge: namedMerger('sourceCodeRepositories'),
            },
            materializedSegment: {
              keyArgs: ['name'],
              merge(existing, incoming, { mergeObjects }) {
                return mergeObjects(existing, incoming);
              },
            },
            clientCredentials: {
              keyArgs: ['parent'],
              merge: namedMerger('clientCredentials'),
            },
            eventDefinitions: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('eventDefinitions'),
            },
            metricCalculations: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('metricCalculations'),
            },
            entities: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('entities'),
            },
            assignmentTables: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('assignmentTables'),
            },
            factTables: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('factTables'),
            },
            dimensionTables: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('dimensionTables'),
            },
            exposureCalculations: {
              keyArgs: ['parent'],
              merge: namedMerger('exposureCalculations'),
            },
            measurementCalculations: {
              keyArgs: ['parent', 'filter'],
              merge: namedMerger('measurementCalculations'),
            },
            events: {
              keyArgs: ['resource'],
              merge: namedMerger('events'),
            },
            resourcePermissions: {
              keyArgs: ['resource'],
              merge: namedMerger('resourcePermissions'),
            },
            scheduledExposureCalculations: {
              keyArgs: [],
              merge: namedMerger('scheduledExposureCalculations'),
            },
            scheduledMetricCalculations: {
              keyArgs: [],
              merge: namedMerger('scheduledMetricCalculations'),
            },
            users: {
              keyArgs: ['filter', 'orderBy'],
              merge: namedMerger('users'),
            },
            userInvitations: {
              keyArgs: [],
              merge: namedMerger('userInvitations'),
            },
            policies: {
              keyArgs: [],
              merge: namedMerger('policies'),
            },
            roles: {
              keyArgs: [],
              merge: namedMerger('roles'),
            },
            presences: {
              keyArgs: [],
              merge: namedMerger('presences'),
            },
            comments: {
              keyArgs: ['filter', 'resource'],
              merge: namedMerger('comments'),
            },
            commentReplies: {
              keyArgs: ['parent'],
              merge: namedMerger('commentReplies'),
            },
            existsDataWarehouses: {
              keyArgs: false,
              merge(existing, incoming, { mergeObjects }) {
                return mergeObjects(existing, incoming);
              },
            },
            feeds: {
              keyArgs: [],
              merge: namedMerger('feeds'),
            },
            feedEntries: {
              keyArgs: ['parent'],
              merge: namedMerger('feedEntries'),
            },
            aiWorkflowTriggers: {
              keyArgs: ['filter'],
              merge: namedMerger('aiWorkflowTriggers'),
            },
          },
        },
      },
    }),
  });

  operationQueueManager.setReadyClient(client);

  if (operationQueueManager.hasQueuedOperations()) {
    setTimeout(() => operationQueueManager.processQueue(), 0);
  }

  return client;
};
